import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles, Card } from 'vopay';
import { Button, PasswordValidationWidget, Title } from 'UI';
import type { OpenDialogProps } from '@/providers/Dialog.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useChangeUserPassword from '@/hooks/useApi/useChangeUserPassword';
import useChangePasswordFormConfig from './useChangePasswordFormConfig';
import styles from './ChangePasswordDialog.module.scss';

const cn = withStyles(styles);

type ChangePasswordDialogProps = OpenDialogProps<boolean> & {
  emailAddress: string;
};

export default function ChangePasswordDialog({ onConfirm, onCancel, emailAddress }: ChangePasswordDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.List.ChangePasswordDialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const [hasValidPassword, setHasValidPassword] = useState(false);
  const { changeUserPassword } = useChangeUserPassword();

  const { ctx, renderFormElements } = useChangePasswordFormConfig({
    onSubmit: async (values) => {
      try {
        await changeUserPassword({ EmailAddress: emailAddress, NewPassword: values.NewPassword, SendEmail: true });
        onConfirm(true);
      } catch {
        onConfirm(false);
      }
    },
  });

  return (
    <Card hoverEffect={false} className={cn('body')}>
      <Title level="3" className={cn('title')}>
        {t('title', { defaultValue: 'Change Password' })}
      </Title>

      <p className={cn('subtitle')}>
        {t('description', { defaultValue: 'Set new password for ' })}
        <span className={cn('subtitleEmail')}>{emailAddress}</span>
      </p>

      <form className={cn('form')} onSubmit={ctx.handleSubmit}>
        {renderFormElements()}

        <PasswordValidationWidget password={ctx.values.NewPassword} onValidate={setHasValidPassword} />

        <div className={cn('footer')}>
          <Button id="change-password-cancel" colorScheme="secondary" type="button" onClick={onCancel}>
            {tGlobal('cancel', { defaultValue: 'Cancel' })}
          </Button>

          <Button
            id="change-password-confirm"
            type="submit"
            colorScheme="primary"
            disabled={isRoleViewOnly || !hasValidPassword || !ctx.isValid || ctx.isSubmitting}
          >
            {tGlobal('confirm', { defaultValue: 'Confirm' })}
          </Button>
        </div>
      </form>
    </Card>
  );
}
